import Vue from 'vue'
import VueRouter from 'vue-router'
import admin_private_url from '../middleware/admin-private-url'
import private_url from '../middleware/private-url'
import public_url from '../middleware/public-url'
import coordinator_private_url from '../middleware/coordinator-private-url'
import admin_only_url from '../middleware/admin-only-url'
import super_admin_only_url from '../middleware/super-admin-only-url'
import store from '@/store'
import { getSnapshot } from '@/plugins/idb'

Vue.use(VueRouter)

// The offline guard legitimately redirects in-flight navigations (e.g. "back" to
// /evaluations while offline → /offline). Vue Router rejects the original push
// promise with a NavigationFailure in that case; unhandled, it surfaces as an
// "Uncaught (in promise) Error: Redirected when going from …" (noise in Sentry).
// Swallow only these expected navigation failures — real errors still reject.
const patchNavigation = method => {
  const original = VueRouter.prototype[method]
  VueRouter.prototype[method] = function patched(location, onResolve, onReject) {
    if (onResolve || onReject) return original.call(this, location, onResolve, onReject)

    return original.call(this, location).catch(err => {
      if (VueRouter.isNavigationFailure(err)) return err

      return Promise.reject(err)
    })
  }
}
patchNavigation('push')
patchNavigation('replace')

const routes = [
  {
    path: '/',
    redirect: { name: 'evaluations' },
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/Home.vue'),
    meta: {
      middleware: private_url,
      layout: 'content',
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/Profile.vue'),
    meta: {
      middleware: private_url,
      layout: 'content',
    },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/Users.vue'),
    meta: {
      middleware: coordinator_private_url,
      layout: 'content',
    },
  },
  {
    path: '/schools',
    name: 'schools',
    component: () => import('@/views/Schools.vue'),
    meta: {
      middleware: private_url,
      layout: 'content',
    },
  },
  {
    path: '/schools/results/:id',
    name: 'schools-results',
    component: () => import('@/components/Schools/SchoolResults.vue'),
    meta: {
      middleware: private_url,
      layout: 'content',
    },
  },
  {
    path: '/evaluations',
    name: 'evaluations',
    component: () => import('@/views/Evaluations.vue'),
    meta: {
      middleware: private_url,
      layout: 'content',
    },
  },
  {
    path: '/evaluations/:id/action-plan',
    name: 'evaluation-action-plan',
    component: () => import('@/views/EvaluationActionPlan.vue'),
    meta: {
      middleware: private_url,
      layout: 'zero',
    },
  },

  {
    path: '/evaluations/:id/scores',
    name: 'evaluation-scores',
    component: () => import('@/views/EvaluationScores.vue'),
    meta: {
      middleware: private_url,
      layout: 'zero',
    },
  },
  // {
  //   path: '/results',
  //   name: 'results',
  //   component: () => import('@/views/Results.vue'),
  //   meta: {
  //     middleware: private_url,
  //     layout: 'content',
  //   },
  // },
  {
    path: '/instruments',
    name: 'instruments',
    component: () => import('@/views/Instruments.vue'),
    meta: {
      middleware: coordinator_private_url,
      layout: 'content',
    },
  },
  {
    path: '/instruments/detail/:id',
    name: 'instrument-detail',
    component: () => import('@/views/InstrumentDetail.vue'),
    meta: {
      middleware: coordinator_private_url,
      layout: 'zero',
    },
  },
  {
    path: '/evaluations/detail/:id/instrument/:instrumentId',
    name: 'evaluation-detail',
    component: () => import('@/views/EvaluationDetail.vue'),
    meta: {
      middleware: private_url,
      layout: 'zero',
    },
  },
  {
    path: '/evaluations/visualization/:id/instrument/:instrumentId',
    name: 'evaluation-visualization',
    component: () => import('@/views/EvaluationVisualization.vue'),
    meta: {
      middleware: private_url,
      layout: 'zero',
    },
  },
  {
    path: '/countries',
    name: 'countries',
    component: () => import('@/views/Countries.vue'),
    meta: {
      middleware: admin_private_url,
      layout: 'content',
    },
  },
  {
    path: '/statistics',
    name: 'statistics',
    component: () => import('@/views/Statistics.vue'),
    meta: {
      middleware: admin_private_url,
      layout: 'content',
    },
  },
  {
    path: '/reports',
    name: 'reports',
    component: () => import('@/views/Reports.vue'),
    meta: {
      middleware: private_url,
      layout: 'content',
    },
  },
  {
    path: '/zones',
    name: 'zones',
    component: () => import('@/views/Zones.vue'),
    meta: {
      middleware: coordinator_private_url,
      layout: 'content',
    },
  },
  {
    path: '/regions',
    name: 'regions',
    component: () => import('@/views/Regions.vue'),
    meta: {
      middleware: admin_only_url,
      layout: 'content',
    },
  },
  {
    path: '/offline-payloads',
    name: 'offline-payloads',
    component: () => import('@/views/OfflinePayloads.vue'),
    meta: {
      middleware: super_admin_only_url,
      layout: 'content',
    },
  },
  {
    path: '/register',
    name: 'auth-register',
    component: () => import('@/views/Register.vue'),
    meta: {
      layout: 'blank',
      resource: 'Public',
      middleware: public_url,
      redirectIfLoggedIn: true,
    },
  },
  {
    path: '/login',
    name: 'auth-login',
    component: () => import('@/views/Login.vue'),
    meta: {
      middleware: public_url,
      layout: 'blank',
    },
  },
  {
    path: '/forgotpassword',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: {
      middleware: public_url,
      layout: 'blank',
    },
  },
  {
    path: '/account/recover',
    name: 'recover-password',
    component: () => import('@/views/RecoverPassword.vue'),
    meta: {
      middleware: public_url,
      layout: 'blank',
    },
  },
  {
    path: '/privacy',
    name: 'privacy-policies',
    component: () => import('@/views/PrivacyPolicies.vue'),
    meta: {
      middleware: public_url,
      layout: 'blank',
    },
  },
  {
    path: '/tutorial',
    name: 'tutorial',
    component: () => import('@/views/Tutorial.vue'),
    meta: {
      middleware: private_url,
      layout: 'content',
    },
  },
  {
    path: '/history',
    name: 'history',
    component: () => import('@/views/History.vue'),
    meta: {
      middleware: private_url,
      layout: 'content',
    },
  },
  {
    path: '/offline',
    name: 'offline-home',
    component: () => import('@/views/OfflineHome.vue'),
    meta: {
      // No middleware: offline the localStorage session may be gone; the home
      // relies on the local snapshot (which carries a user profile) instead.
      layout: 'zero',
    },
  },
  {
    path: '/error-404',
    name: 'error-404',
    component: () => import('@/views/Error404.vue'),
    meta: {
      layout: 'blank',
      resource: 'Public',
    },
  },
  {
    path: '*',
    redirect: 'error-404',
  },
]

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') throw err
  })
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
})

const CHUNK_RELOAD_FLAG = 'chunk_reload_attempted'

router.onError((error) => {
  const isChunkError =
    error.name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(error.message) ||
    /Loading CSS chunk \d+ failed/i.test(error.message)

  if (!isChunkError) return

  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
    return // Ya se intentó recargar — no hacer loop
  }

  sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1')
  window.location.reload()
})

function nextFactory(context, middleware, index) {
  const subsequentMiddleware = middleware[index];
  if (!subsequentMiddleware) return context.next;

  return (...parameters) => {
    // Run the default Vue Router `next()` callback first.
    context.next(...parameters);
    // Then run the subsequent Middleware with a new
    // `nextMiddleware()` callback.
    const nextMiddleware = nextFactory(context, middleware, index + 1);
    subsequentMiddleware({ ...context, next: nextMiddleware });
  };
}

router.beforeEach(async (to, from, next) => {
  // Offline: the app shell loads from the SW cache but every API-backed route is
  // useless without a network. Funnel all of them to the offline home, which reads
  // only from IndexedDB. Public (blank) pages and the offline home itself pass through.
  // Source of truth is the store (backend reachability via axios), falling back to
  // navigator.onLine — which lies on a LAN with no internet.
  const navigatorOffline = typeof navigator !== 'undefined' && !navigator.onLine
  const isOffline = navigatorOffline || !store.getters['offline/isOnline']
  if (isOffline && to.name !== 'offline-home' && to.meta.layout !== 'blank') {
    // Exception: opening a downloaded assessment's detail is allowed offline —
    // EvaluationDetail feeds itself from the local snapshot in that case.
    const lineId = Number(to.query.line)
    let isDownloadedDetail = to.name === 'evaluation-detail'
      && !!lineId
      && store.getters['offline/isDownloadedLocally'](lineId)
    // The store may not be hydrated yet on a fresh load / reload (refreshOfflineState
    // is async). Fall back to IndexedDB directly so reloading the offline detail
    // stays put instead of bouncing to the offline home.
    if (!isDownloadedDetail && to.name === 'evaluation-detail' && !!lineId) {
      try {
        isDownloadedDetail = !!(await getSnapshot(lineId))
      } catch (e) {
        isDownloadedDetail = false
      }
    }
    if (!isDownloadedDetail) {
      return next({ name: 'offline-home' })
    }
  }

  if (to.meta.middleware) {
    const middleware = Array.isArray(to.meta.middleware)
      ? to.meta.middleware
      : [to.meta.middleware];

    const context = {
      from,
      next,
      router,
      to,
    };
    const nextMiddleware = nextFactory(context, middleware, 1);

    return middleware[0]({ ...context, next: nextMiddleware });
  }

  return next();
});

export default router
